import { universalStorage } from '../../storage/universalStorage';
import { v4 as uuidv4 } from 'uuid';

export interface Teacher {
  id: string;
  firstName: string;
  lastName: string;
  fatherName?: string;
  gender?: string;
  position?: string;
  idNumber?: string;
  email: string;
  phone?: string;
  country?: string;
  city?: string;
  address?: string;
  age?: string;
  dateOfBirth?: string;
  maritalStatus?: string;
  departments: string[];
  subjects: string[];
  employmentType: 'Full-time' | 'Part-time' | 'Contract';
  salary?: number;
  hireDate: string;
  status: 'Active' | 'Inactive' | 'On Leave';
  emergencyContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  qualifications: string[];
  documents: any[]; // Changed to any[] to support both string and object formats
  paymentHistory: Array<{
    id: string;
    amount: number;
    date: string;
    type: 'Salary' | 'Bonus' | 'Overtime';
    status: 'Paid' | 'Pending';
  }>;
  attendance: Array<{
    date: string;
    status: 'Present' | 'Absent' | 'Late';
    hoursWorked?: number;
  }>;
  history: Array<{
    id: string;
    date: string;
    action: string;
    details: string;
  }>;
  profileImage?: string;
  bio?: string;
  createdAt: string;
  updatedAt: string;
}

class TeachersFileStorageService {
  private readonly STORAGE_KEY = 'nalc_teachers';
  private readonly TARGET_TEACHER_CAPACITY = 5000;
  
  private extractArrayFromStorageData(data: any): Teacher[] {
    // Handle different data formats that might be stored
    if (!data) return [];
    
    // If it's already an array, return it
    if (Array.isArray(data)) return data;
    
    // If it's wrapped with metadata (version 3.0 format)
    if (data.data && typeof data.data === 'string') {
      try {
        const parsed = JSON.parse(data.data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Error parsing wrapped teacher data:', error);
        return [];
      }
    }
    
    // If it's wrapped but data is already parsed
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // If it has a teachers property
    if (data.teachers && Array.isArray(data.teachers)) {
      return data.teachers;
    }
    
    console.warn('Unknown teacher data format:', data);
    return [];
  }
  
  async getAllTeachers(): Promise<Teacher[]> {
    try {
      console.log('👨‍🏫 Loading teachers with enhanced large data storage...');
      const rawData = await universalStorage.getItem<any>(this.STORAGE_KEY);
      const teachers = this.extractArrayFromStorageData(rawData);
      
      if (teachers.length > 0) {
        console.log(`✅ Loaded ${teachers.length} teachers successfully`);
        
        // Ensure all teachers have proper structure
        const validTeachers = teachers.filter(teacher => teacher && teacher.id).map(teacher => ({
          id: teacher.id,
          firstName: teacher.firstName || '',
          lastName: teacher.lastName || '',
          fatherName: teacher.fatherName || '',
          gender: teacher.gender || '',
          position: teacher.position || '',
          idNumber: teacher.idNumber || '',
          email: teacher.email || '',
          phone: teacher.phone || '',
          country: teacher.country || 'Afghanistan',
          city: teacher.city || '',
          address: teacher.address || '',
          age: teacher.age || '',
          dateOfBirth: teacher.dateOfBirth || '',
          maritalStatus: teacher.maritalStatus || '',
          departments: teacher.departments || [],
          subjects: teacher.subjects || [],
          employmentType: teacher.employmentType || 'Full-time',
          salary: teacher.salary || 0,
          hireDate: teacher.hireDate || new Date().toISOString(),
          status: teacher.status || 'Active',
          emergencyContact: teacher.emergencyContact,
          qualifications: teacher.qualifications || [],
          documents: teacher.documents || [], // Keep original document format
          paymentHistory: teacher.paymentHistory || [],
          attendance: teacher.attendance || [],
          history: teacher.history || [],
          profileImage: teacher.profileImage || '',
          bio: teacher.bio || '',
          createdAt: teacher.createdAt || new Date().toISOString(),
          updatedAt: teacher.updatedAt || new Date().toISOString()
        }));
        
        if (validTeachers.length !== teachers.length) {
          console.log(`Fixed ${teachers.length - validTeachers.length} teachers with missing data`);
          await this.saveTeachers(validTeachers);
        }
        
        return validTeachers;
      }
      
      console.log('👨‍🏫 No teachers found, returning empty array');
      return [];
    } catch (error) {
      console.error('Error loading teachers with enhanced storage:', error);
      console.log('👨‍🏫 Initializing empty teachers storage');
      return [];
    }
  }
  
  private async saveTeachers(teachers: Teacher[]): Promise<void> {
    try {
      // Use enhanced storage with compression and quota handling
      await universalStorage.setItem(this.STORAGE_KEY, teachers);
      console.log(`✅ Saved ${teachers.length} teachers with enhanced storage (${teachers.length}/${this.TARGET_TEACHER_CAPACITY} capacity)`);
    } catch (error) {
      console.error('Error saving teachers with enhanced storage:', error);
      throw new Error(`Failed to save teachers: ${error.message}`);
    }
  }
  
  async getTeacherById(id: string): Promise<Teacher | null> {
    try {
      const teachers = await this.getAllTeachers();
      const teacher = teachers.find(teacher => teacher.id === id);
      
      if (teacher) {
        console.log(`✅ Found teacher: ${teacher.firstName} ${teacher.lastName} (ID: ${id})`);
      } else {
        console.log(`❌ Teacher not found with ID: ${id}`);
      }
      
      return teacher || null;
    } catch (error) {
      console.error(`Error loading teacher ${id}:`, error);
      return null;
    }
  }
  
  async addTeacher(teacherData: any): Promise<Teacher> {
    try {
      console.log('👨‍🏫 Adding teacher with enhanced storage...');
      
      const teachers = await this.getAllTeachers();
      const now = new Date().toISOString();
      
      const newTeacher: Teacher = {
        id: teacherData.id || uuidv4(),
        firstName: teacherData.firstName || '',
        lastName: teacherData.lastName || '',
        fatherName: teacherData.fatherName || '',
        gender: teacherData.gender || '',
        position: teacherData.position || '',
        idNumber: teacherData.idNumber || '',
        email: teacherData.email || '',
        phone: teacherData.phone || '',
        country: teacherData.country || 'Afghanistan',
        city: teacherData.city || '',
        address: teacherData.address || '',
        age: teacherData.age || '',
        dateOfBirth: teacherData.dateOfBirth || '',
        maritalStatus: teacherData.maritalStatus || '',
        departments: Array.isArray(teacherData.departments) 
          ? teacherData.departments.map((d: any) => typeof d === 'string' ? d : d.name || '').filter(Boolean)
          : [],
        subjects: teacherData.subjects || [],
        employmentType: teacherData.employmentType || 'Full-time',
        salary: typeof teacherData.salary === 'string' ? parseFloat(teacherData.salary) || 0 : teacherData.salary || 0,
        hireDate: teacherData.hireDate || teacherData.joinDate || now,
        status: teacherData.status || 'Active',
        emergencyContact: teacherData.emergencyContact,
        qualifications: teacherData.qualifications || [],
        documents: teacherData.documents || [], // Keep original format - could be strings or objects
        paymentHistory: teacherData.paymentHistory || teacherData.payments || [],
        attendance: teacherData.attendance || [],
        history: teacherData.history || [],
        profileImage: teacherData.profileImage || '',
        bio: teacherData.bio || '',
        createdAt: teacherData.createdAt || now,
        updatedAt: now
      };
      
      console.log('🖼️ Adding teacher with profileImage:', newTeacher.profileImage ? 'YES' : 'NO');
      console.log('📄 Adding teacher with documents:', newTeacher.documents.length, 'documents');
      
      teachers.push(newTeacher);
      
      await this.saveTeachers(teachers);
      
      console.log(`✅ Teacher added successfully: ${newTeacher.firstName} ${newTeacher.lastName} (${teachers.length}/${this.TARGET_TEACHER_CAPACITY} capacity)`);
      
      return newTeacher;
    } catch (error) {
      console.error('Error adding teacher:', error);
      throw new Error(`Failed to add teacher: ${error.message}`);
    }
  }
  
  async updateTeacher(id: string, teacherData: any): Promise<Teacher | null> {
    try {
      console.log(`👨‍🏫 Updating teacher ${id} with enhanced storage...`);
      
      const teachers = await this.getAllTeachers();
      const teacherIndex = teachers.findIndex(teacher => teacher.id === id);
      
      if (teacherIndex === -1) {
        console.log(`❌ Teacher not found for update: ID ${id}`);
        return await this.addTeacher({ ...teacherData, id });
      }
      
      const updatedTeacher: Teacher = {
        ...teachers[teacherIndex],
        firstName: teacherData.firstName || teachers[teacherIndex].firstName,
        lastName: teacherData.lastName || teachers[teacherIndex].lastName,
        fatherName: teacherData.fatherName || teachers[teacherIndex].fatherName,
        gender: teacherData.gender || teachers[teacherIndex].gender,
        position: teacherData.position || teachers[teacherIndex].position,
        idNumber: teacherData.idNumber || teachers[teacherIndex].idNumber,
        email: teacherData.email || teachers[teacherIndex].email,
        phone: teacherData.phone || teachers[teacherIndex].phone,
        country: teacherData.country || teachers[teacherIndex].country,
        city: teacherData.city || teachers[teacherIndex].city,
        address: teacherData.address || teachers[teacherIndex].address,
        age: teacherData.age || teachers[teacherIndex].age,
        dateOfBirth: teacherData.dateOfBirth || teachers[teacherIndex].dateOfBirth,
        maritalStatus: teacherData.maritalStatus || teachers[teacherIndex].maritalStatus,
        departments: Array.isArray(teacherData.departments) 
          ? teacherData.departments.map((d: any) => typeof d === 'string' ? d : d.name || '').filter(Boolean)
          : teachers[teacherIndex].departments,
        subjects: teacherData.subjects || teachers[teacherIndex].subjects,
        employmentType: teacherData.employmentType || teachers[teacherIndex].employmentType,
        salary: typeof teacherData.salary === 'string' ? parseFloat(teacherData.salary) || 0 : teacherData.salary || teachers[teacherIndex].salary,
        hireDate: teacherData.hireDate || teacherData.joinDate || teachers[teacherIndex].hireDate,
        status: teacherData.status || teachers[teacherIndex].status,
        qualifications: teacherData.qualifications || teachers[teacherIndex].qualifications,
        documents: teacherData.documents !== undefined ? teacherData.documents : teachers[teacherIndex].documents, // Preserve documents properly
        paymentHistory: teacherData.paymentHistory || teacherData.payments || teachers[teacherIndex].paymentHistory,
        history: teacherData.history || teachers[teacherIndex].history,
        profileImage: teacherData.profileImage !== undefined ? teacherData.profileImage : teachers[teacherIndex].profileImage,
        bio: teacherData.bio !== undefined ? teacherData.bio : teachers[teacherIndex].bio,
        id: id,
        updatedAt: new Date().toISOString(),
        createdAt: teachers[teacherIndex].createdAt
      };
      
      console.log('🖼️ Updating teacher with profileImage:', updatedTeacher.profileImage ? 'YES' : 'NO');
      console.log('📄 Updating teacher with documents:', updatedTeacher.documents.length, 'documents');
      
      teachers[teacherIndex] = updatedTeacher;
      await this.saveTeachers(teachers);
      
      console.log(`✅ Teacher updated successfully: ${updatedTeacher.firstName} ${updatedTeacher.lastName}`);
      
      return updatedTeacher;
    } catch (error) {
      console.error(`Error updating teacher ${id}:`, error);
      throw error;
    }
  }
  
  async deleteTeacher(id: string): Promise<void> {
    try {
      console.log(`👨‍🏫 Deleting teacher ${id}...`);
      
      const teachers = await this.getAllTeachers();
      const filteredTeachers = teachers.filter(teacher => teacher.id !== id);
      
      if (filteredTeachers.length === teachers.length) {
        console.log(`❌ Teacher not found for deletion: ID ${id}`);
        return;
      }
      
      await this.saveTeachers(filteredTeachers);
      
      console.log(`✅ Teacher deleted successfully (${filteredTeachers.length} teachers remaining)`);
    } catch (error) {
      console.error(`Error deleting teacher ${id}:`, error);
      throw error;
    }
  }

  async searchTeachers(query: string): Promise<Teacher[]> {
    try {
      const teachers = await this.getAllTeachers();
      const lowerQuery = query.toLowerCase();
      
      return teachers.filter(teacher => 
        teacher.firstName.toLowerCase().includes(lowerQuery) ||
        teacher.lastName.toLowerCase().includes(lowerQuery) ||
        teacher.email.toLowerCase().includes(lowerQuery) ||
        (teacher.phone && teacher.phone.includes(lowerQuery)) ||
        teacher.departments.some(dept => dept.toLowerCase().includes(lowerQuery)) ||
        teacher.subjects.some(subject => subject.toLowerCase().includes(lowerQuery))
      );
    } catch (error) {
      console.error('Error searching teachers:', error);
      return [];
    }
  }

  async getStorageStats(): Promise<{
    totalTeachers: number;
    storageUsed: string;
    maxCapacity: number;
    isOptimized: boolean;
    compressionRatio?: number;
  }> {
    try {
      const teachers = await this.getAllTeachers();
      const storageInfo = await universalStorage.getStorageInfo();
      
      return {
        totalTeachers: teachers.length,
        storageUsed: `${(storageInfo.usage?.used || 0 / 1024).toFixed(2)} KB`,
        maxCapacity: this.TARGET_TEACHER_CAPACITY,
        isOptimized: teachers.length > 100,
        compressionRatio: storageInfo.metrics?.compressionRatio
      };
    } catch (error) {
      return {
        totalTeachers: 0,
        storageUsed: '0 KB',
        maxCapacity: this.TARGET_TEACHER_CAPACITY,
        isOptimized: false
      };
    }
  }

  async addTeacherHistoryEntry(teacherId: string, action: string, details: string): Promise<void> {
    try {
      const teacher = await this.getTeacherById(teacherId);
      
      if (teacher) {
        const historyEntry = {
          id: uuidv4(),
          date: new Date().toISOString(),
          action,
          details
        };
        
        teacher.history = [...(teacher.history || []), historyEntry];
        await this.updateTeacher(teacherId, { history: teacher.history });
        
        console.log(`✅ Added history entry for teacher ${teacherId}`);
      }
    } catch (error) {
      console.error(`Failed to add history entry for teacher ${teacherId}:`, error);
      throw error;
    }
  }
}

export const teachersFileStorageService = new TeachersFileStorageService();
